import { Button } from "@/components/ui/button";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import heroImage from "@assets/generated_images/authentic_cuban_point_of_sale_with_qvapos_display.png";
import { motion } from "framer-motion";

export default function Hero() {
  return (
    <section className="relative pt-20 sm:pt-24 md:pt-32 lg:pt-48 pb-8 sm:pb-10 lg:pb-20 overflow-hidden">
      {/* Dynamic Background Elements */}
      <div className="absolute top-0 left-0 w-full h-full -z-10 overflow-hidden">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-primary/10 rounded-full blur-[120px] animate-pulse" />
        <div className="absolute bottom-[10%] right-[-5%] w-[30%] h-[30%] bg-secondary/10 rounded-full blur-[100px]" />
        <div className="absolute top-[20%] right-[20%] w-[15%] h-[15%] bg-blue-400/5 rounded-full blur-[80px]" />
      </div>

      <div className="container px-4 mx-auto relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="text-center lg:text-left"
          >
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/60 dark:bg-slate-800/60 backdrop-blur-md border border-primary/10 text-primary text-xs font-bold mb-8 shadow-sm tracking-widest uppercase"
            >
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
              </span>
              Liderando la Innovación POS en Cuba
            </motion.div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold tracking-tight text-foreground mb-5 sm:mb-6 md:mb-8 leading-[1.2] sm:leading-[1.15] font-display">
              Gestiona con precisión. <br />
              <span className="text-gradient">Encuentra sin caminar.</span>
            </h1>

            <p className="text-base sm:text-lg md:text-xl text-muted-foreground mb-8 sm:mb-10 max-w-xl mx-auto lg:mx-0 leading-relaxed font-light px-4 sm:px-0">
              El ecosistema que transforma el comercio. Si tienes un negocio,
              automatiza tu inventario, detén las fugas y escala tus ingresos.
              Si buscas comprar, descubre en tiempo real qué venden los locales
              cerca de ti con precios y stock actualizados.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-3 sm:gap-4 mb-8 sm:mb-10 lg:mb-12">
              <Button
                onClick={() =>
                  (window.location.href = "http://qvapos.cu/register")
                }
                className="w-full sm:w-auto text-sm sm:text-base font-bold px-6 sm:px-8 md:px-10 h-12 sm:h-14 md:h-16 rounded-full bg-primary hover:bg-primary/90 shadow-[0_20px_40px_-15px_rgba(59,130,246,0.35)] transition-all hover:scale-[1.02] active:scale-[0.98]"
              >
                Tengo un negocio
                <ArrowRight className="ml-2 h-4 w-4 sm:h-5 sm:w-5" />
              </Button>
              <Button
                onClick={() =>
                  (window.location.href = "http://qvapos.cu/home")
                }
                className="w-full sm:w-auto text-sm sm:text-base font-bold px-6 sm:px-8 md:px-10 h-12 sm:h-14 md:h-16 rounded-full transition-all hover:scale-[1.02] active:scale-[0.98] bg-orange-500 hover:bg-orange-600 text-white border-0 shadow-xl shadow-orange-500/20"
              >
                Ver ofertas cerca
              </Button>
            </div>

            <div className="flex flex-wrap justify-center lg:justify-start gap-4 sm:gap-6 md:gap-8">
              <div className="flex items-center gap-2 sm:gap-3 group">
                <div className="h-6 w-6 sm:h-7 md:h-8 sm:w-7 md:w-8 rounded-full bg-secondary/10 flex items-center justify-center group-hover:bg-secondary/20 transition-colors">
                  <CheckCircle2 className="h-3 w-3 sm:h-3.5 sm:w-3.5 md:h-4 md:w-4 text-secondary" />
                </div>
                <span className="text-xs sm:text-sm font-medium text-slate-600">
                  Instalación Flash
                </span>
              </div>
              <div className="flex items-center gap-2 sm:gap-3 group">
                <div className="h-6 w-6 sm:h-7 md:h-8 sm:w-7 md:w-8 rounded-full bg-secondary/10 flex items-center justify-center group-hover:bg-secondary/20 transition-colors">
                  <CheckCircle2 className="h-3 w-3 sm:h-3.5 sm:w-3.5 md:h-4 md:w-4 text-secondary" />
                </div>
                <span className="text-xs sm:text-sm font-medium text-slate-600">
                  Soporte Premium
                </span>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1, ease: "easeOut", delay: 0.1 }}
            className="relative perspective-2000"
          >
            <div className="relative group">
              {/* Decorative rings */}
              <div className="absolute -inset-4 bg-gradient-to-tr from-primary/20 to-secondary/20 rounded-[2.5rem] blur-2xl opacity-30 group-hover:opacity-50 transition-opacity duration-700" />

              <div className="relative rounded-2xl sm:rounded-[2rem] overflow-hidden shadow-[0_32px_64px_-16px_rgba(0,0,0,0.15)] border border-white/50 bg-white group-hover:rotate-1 transition-transform duration-700 ease-out">
                <img
                  src={heroImage}
                  alt="Punto de Venta QvaPOS"
                  className="w-full h-auto object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent pointer-events-none" />
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
